import { useEffect, useRef, useState } from 'react';
import { AUTOCOMPLETE_DEBOUNCE_MS, type CommanderPick, cardToCommanderPick } from '@edh/shared';
import { scryfallAutocomplete, scryfallCard } from '../lib/api';

/**
 * Spec 3b — registering a deck should feel like naming a commander, not filling
 * in a form. Colour identity comes free from the card, which is why there are no
 * WUBRG buttons anywhere in this app: the commander is right about the fiddly
 * cases a human gets wrong.
 */
export function CommanderSearch({
  label = 'Commander',
  placeholder = "Atraxa, Praetors' Voice",
  onPick,
  onManual,
}: {
  /**
   * What this field is called. The caller decides, because the second one on
   * the deck sheet is the partner and calling it "Commander" would be wrong —
   * which is how this ended up owning its own label, and how the sheet ended up
   * with the word twice: a `<label>` here inside a `<label>` there.
   */
  label?: string;
  placeholder?: string;
  onPick: (pick: CommanderPick) => void;
  onManual: (name: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState(false);
  const timer = useRef<number>(0);

  useEffect(() => {
    if (picked || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    // Spec 3b — debounce ~250ms. A 429 locks you out for thirty seconds, and
    // the whole group shares one proxy.
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      scryfallAutocomplete(query.trim())
        .then(setSuggestions)
        .catch(() => setError('offline — you can still type the name yourself'))
        .finally(() => setLoading(false));
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer.current);
  }, [query, picked]);

  async function choose(name: string) {
    setPicked(true);
    setSuggestions([]);
    setLoading(true);
    try {
      const card = await scryfallCard(name);
      onPick(cardToCommanderPick(card));
      setQuery(card.name);
    } catch {
      setError('could not fetch that card — saved as a plain name');
      onManual(name);
      setQuery(name);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <label className="field">
        <span>
          {label} {loading && <span className="spinner" style={{ marginLeft: 6 }} />}
        </span>
        <input
          className="input"
          value={query}
          placeholder={placeholder}
          autoCapitalize="words"
          autoCorrect="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setPicked(false);
            onManual(e.target.value);
          }}
        />
      </label>

      {suggestions.length > 0 && (
        <div className="suggestions">
          {suggestions.slice(0, 7).map((name) => (
            <button key={name} type="button" onClick={() => void choose(name)}>
              {name}
            </button>
          ))}
        </div>
      )}

      {/* Spec 3b — keep a manual path for decks with no single commander card,
          or for anyone registering a deck offline. */}
      {error && <p className="hint" style={{ marginTop: -6, marginBottom: 12 }}>{error}</p>}
    </>
  );
}
